import { Loader2, User, Pencil, Trash2, Send } from 'lucide-react'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils/helpers'
import { formatPhoneDisplay } from '../utils/formatPhone'
import { staffDisplayName } from '../utils/propertyContactsLogic'
import { getRoleDisplayName } from '@/constants/roles'
import { formatDate } from '@/lib/utils/formatters'
import { CONTACT_TABLE_SCROLL_AFTER, CONTACT_TABLE_SCROLL_MAX_H } from '../constants'
import type { PropertyContactsStaffTableProps } from '../types'
import type { CompanyStaffMember } from '@/features/company/types'

const staffActionButtonClass =
  'inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60'

function formatLastActiveDate(dateVal: string | number | null | undefined): string {
  if (!dateVal) return ''
  return formatDate(dateVal, 'MM/dd/yyyy HH:mm:ss')
}

function StaffRowActions({
  staff,
  propertyId,
  canShowStaffActions,
  canImpersonate,
  isLibertyRole,
  canRemoveRole,
  onImpersonate,
  onEditUser,
  onRemoveRole,
  onWelcomeEmail,
}: {
  staff: CompanyStaffMember
  propertyId: string | number | undefined
  canShowStaffActions: boolean
  canImpersonate: boolean
  isLibertyRole: boolean
  canRemoveRole: boolean
  onImpersonate: (roleId: number) => void
  onEditUser: (userId: string | number) => void
  onRemoveRole: (staff: CompanyStaffMember) => void
  onWelcomeEmail: (staff: CompanyStaffMember) => void
}) {
  if (!canShowStaffActions) return null
  return (
    <>
      {isLibertyRole && canImpersonate && !!staff.role_id && (
        <button
          type="button"
          onClick={() => void onImpersonate(Number(staff.role_id))}
          data-testid={`property-contacts-staff-impersonate-${staff.role_id}-button`}
          className={staffActionButtonClass}
          title="Impersonate"
        >
          <User size={14} />
        </button>
      )}
      <button
        type="button"
        data-testid={`property-contacts-staff-edit-${staff.user_id}-button`}
        onClick={() => staff.user_id != null && onEditUser(staff.user_id)}
        className={staffActionButtonClass}
        title="Edit"
      >
        <Pencil size={14} />
      </button>
      {canRemoveRole && (
        <button
          type="button"
          onClick={() => onRemoveRole(staff)}
          data-testid={`property-contacts-staff-remove-${staff.role_id}-button`}
          className={staffActionButtonClass}
          title="Remove"
        >
          <Trash2 size={14} />
        </button>
      )}
      {!staff.user_password && propertyId && (
        <button
          type="button"
          onClick={() => onWelcomeEmail(staff)}
          data-testid={`property-contacts-staff-welcome-${staff.user_id}-button`}
          className={staffActionButtonClass}
          title="Send Welcome Email"
        >
          <Send size={14} />
        </button>
      )}
    </>
  )
}

export function PropertyContactsStaffTable({
  staffLoading,
  propertyStaff,
  propertyId,
  showSelectBox,
  selectedUserIds,
  selectableUserIdsForEmail,
  allEmailRowsSelected,
  someEmailRowsSelected,
  canShowStaffActions,
  canLogCall,
  canImpersonate,
  isLibertyRole,
  canRemoveRole,
  canEdit,
  onToggleSelectAllEmail,
  onToggleUserSelection,
  onLogCall,
  onImpersonate,
  onEditUser,
  onRemoveRole,
  onWelcomeEmail,
}: PropertyContactsStaffTableProps) {
  const isMobile = useIsMobile()

  if (staffLoading) {
    return (
      <div data-testid="property-contacts-loading" className="flex items-center gap-2 py-4">
        <Loader2 className="h-[18px] w-[18px] animate-spin text-[rgb(21_151_255)]" aria-hidden />
        <span className="text-sm text-slate-500">Loading contacts…</span>
      </div>
    )
  }

  if (propertyStaff.length === 0) {
    return (
      <p data-testid="property-contacts-empty" className="text-sm text-slate-500">
        No contacts found.
      </p>
    )
  }

  if (isMobile) {
    return (
      <div data-testid="property-contacts-grid" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {propertyStaff.map((s, i) => {
          const rowKey = String(s.user_id ?? s.role_id ?? `idx-${i}`)
          const displayName = staffDisplayName(s)
          return (
            <div
              key={rowKey}
              data-testid={`property-contacts-card-${rowKey}`}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="mb-3 flex items-start justify-between gap-2">
                <button
                  type="button"
                  data-testid={`property-contacts-card-${rowKey}-name`}
                  className={`text-left text-sm font-semibold text-[rgb(21_151_255)] ${
                    canLogCall ? 'underline' : ''
                  }`}
                  onClick={() => canLogCall && onLogCall(s)}
                >
                  {displayName || '—'}
                </button>
                {showSelectBox && canEdit && s.user_id != null && (
                  <input
                    type="checkbox"
                    data-testid={`property-contacts-card-${rowKey}-email-checkbox`}
                    checked={selectedUserIds.includes(Number(s.user_id))}
                    onChange={() => onToggleUserSelection(Number(s.user_id))}
                    aria-label={`Select ${displayName || 'staff'}`}
                  />
                )}
              </div>
              <dl className="space-y-1.5 text-xs text-slate-600">
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-400">Role</dt>
                  <dd>{s.role_name ? getRoleDisplayName(s.role_name) : '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-400">Phone</dt>
                  <dd>{formatPhoneDisplay(s.mobile_no as string) || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-400">Email</dt>
                  <dd className="truncate">{String(s.email ?? '—')}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-400">Last Activity</dt>
                  <dd>{formatLastActiveDate(s.last_active) || '—'}</dd>
                </div>
              </dl>
              {canShowStaffActions && (
                <div className="mt-3 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
                  <StaffRowActions
                    staff={s}
                    propertyId={propertyId}
                    canShowStaffActions={canShowStaffActions}
                    canImpersonate={canImpersonate}
                    isLibertyRole={isLibertyRole}
                    canRemoveRole={canRemoveRole}
                    onImpersonate={onImpersonate}
                    onEditUser={onEditUser}
                    onRemoveRole={onRemoveRole}
                    onWelcomeEmail={onWelcomeEmail}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const needsVerticalScroll = propertyStaff.length > CONTACT_TABLE_SCROLL_AFTER

  return (
    <div
      data-testid="property-contacts-table-wrap"
      className={cn(
        'rounded-lg border border-slate-200',
        needsVerticalScroll
          ? cn(CONTACT_TABLE_SCROLL_MAX_H, 'overflow-x-auto overflow-y-auto')
          : 'overflow-x-auto',
      )}
    >
      <table className="w-full min-w-[600px] border-collapse text-[13px]" data-testid="property-contacts-staff-table">
        <thead
          data-testid="property-contacts-table-head"
          className={cn(
            needsVerticalScroll
              ? 'sticky top-0 z-[1] bg-slate-50 shadow-[0_1px_0_0_rgb(226_232_240)]'
              : 'bg-slate-50/80',
          )}
        >
          <tr className="border-b border-slate-200">
            {showSelectBox && canEdit && (
              <th className="w-11 px-3 py-2.5 text-center text-xs font-medium text-slate-500">
                <input
                  type="checkbox"
                  disabled={selectableUserIdsForEmail.length === 0}
                  checked={allEmailRowsSelected}
                  onChange={onToggleSelectAllEmail}
                  aria-label="Select all contacts for email"
                  data-testid="property-contacts-select-all-checkbox"
                  ref={(el) => {
                    if (!el) return
                    el.indeterminate = someEmailRowsSelected && !allEmailRowsSelected
                  }}
                />
              </th>
            )}
            <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500">Name</th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500">Role</th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500">Phone Number</th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500">Email</th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500">Last Activity</th>
            {canShowStaffActions && (
              <th className="w-0 px-3 py-2.5 text-left text-xs font-medium whitespace-nowrap text-slate-500">
                Action
              </th>
            )}
          </tr>
        </thead>
        <tbody data-testid="property-contacts-table-body">
          {propertyStaff.map((s, i) => {
            const rowKey = String(s.user_id ?? s.role_id ?? `idx-${i}`)
            return (
              <tr
                key={rowKey}
                data-testid={`property-contacts-row-${rowKey}`}
                className="border-b border-slate-100 last:border-b-0"
              >
                {showSelectBox && canEdit && (
                  <td className="px-3 py-3 align-middle">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(Number(s.user_id))}
                      data-testid={`property-contacts-staff-row-${s.user_id}-checkbox`}
                      onChange={() => onToggleUserSelection(Number(s.user_id))}
                      aria-label={`Select ${staffDisplayName(s)}`}
                    />
                  </td>
                )}
                <td
                  data-testid={`property-contacts-row-${rowKey}-name-cell`}
                  className={`px-3 py-3 font-medium text-[rgb(21_151_255)] ${
                    canLogCall ? 'cursor-pointer underline' : 'cursor-default'
                  }`}
                  onClick={() => canLogCall && onLogCall(s)}
                >
                  {staffDisplayName(s)}
                </td>
                <td className="px-3 py-3 text-slate-500">
                  {s.role_name ? getRoleDisplayName(s.role_name) : '—'}
                </td>
                <td className="px-3 py-3 text-slate-500">
                  {formatPhoneDisplay(s.mobile_no as string) || '—'}
                </td>
                <td className="px-3 py-3 text-slate-500">{String(s.email ?? '—')}</td>
                <td className="px-3 py-3 text-slate-500">
                  {formatLastActiveDate(s.last_active) || '—'}
                </td>
                {canShowStaffActions && (
                  <td className="w-0 px-3 py-3 whitespace-nowrap">
                    <div className="flex gap-1.5">
                      <StaffRowActions
                        staff={s}
                        propertyId={propertyId}
                        canShowStaffActions={canShowStaffActions}
                        canImpersonate={canImpersonate}
                        isLibertyRole={isLibertyRole}
                        canRemoveRole={canRemoveRole}
                        onImpersonate={onImpersonate}
                        onEditUser={onEditUser}
                        onRemoveRole={onRemoveRole}
                        onWelcomeEmail={onWelcomeEmail}
                      />
                    </div>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
